import { RegistrationData } from "../types/registrationTypes";
import {
  AdminRequestInfo,
  CompanyRequestInfo,
  RegisterRequest
} from "../../../Graphql/types/register";
import * as yup from "yup";
import {
  COUNTRY_CODE,
  getCompanyAddressSchema,
  getCompanyCurrencySchema,
  isValidPhoneNumberForCountry,
  PhoneCountryCodeMap,
  combinePhoneNumber,
  EMAIL_REGEX,
  sanitizeCompanyAddressForSave
} from "@kivunova/kivufrontendcommon";
import { CountryCode } from "libphonenumber-js";
import { getRegistrationBusinessTypeValues } from "./registrationBusinessTypeOptions";

export const transformRegistrationToRequest = (
  data: RegistrationData
): RegisterRequest => {
  const { admin, company } = data;

  const fullPhoneNumber = combinePhoneNumber(
    admin.phoneCountryCode,
    admin.phone,
    PhoneCountryCodeMap
  );

  const adminRequest: AdminRequestInfo = {
    firstName: admin.firstName,
    lastName: admin.lastName,
    email: admin.email,
    phone: fullPhoneNumber,
    password: admin.password,
    profileImage: admin.profileImage
  };

  const companyRequest: CompanyRequestInfo = {
    name: company.name,
    businessType: company.businessType,
    currencyCode: company.currencyCode,
    registrationNumber: company.registrationNumber,
    description: company.description,
    vatPercentage: company.vatPercentage,
    vatCalculationMode: company.vatCalculationMode,
    address: {
      ...sanitizeCompanyAddressForSave(company.address),
      country: company.address.country
    }
  };

  return {
    admin: adminRequest,
    company: companyRequest
  };
};

export const getRegistrationAddressSchema = getCompanyAddressSchema;

export const getRegistrationAdminSchema = (
  t: (key: string, value: string, opt?: { [key: string]: string }) => string
) => {
  return yup.object({
    admin: yup.object({
      firstName: yup
        .string()
        .required(
          t("first-name-required-error-message", "First name is required")
        ),
      lastName: yup
        .string()
        .required(
          t("lastname-required-error-message", "Last name is required")
        ),
      phoneCountryCode: yup
        .string()
        .required(
          t(
            "phone-country-code-required-error-message",
            "Phone country code is required"
          )
        ),
      email: yup
        .string()
        .required(t("email-required-error-message", "Email is required"))
        .matches(
          EMAIL_REGEX,
          t("email-invalid-error-message", "Enter valid email")
        ),
      phone: yup
        .string()
        .required(
          t("phone number-required-error-message", "Phone number is required")
        )
        .test(
          "is-valid-phone",
          t("phone-invalid-error-message", "Enter a valid phone number"),
          function (value) {
            if (!value) return false;
            const { phoneCountryCode } = this.parent as {
              phoneCountryCode?: string;
            };

            const countryCode = phoneCountryCode
              ? PhoneCountryCodeMap[phoneCountryCode].code
              : COUNTRY_CODE.RWANDA;
            const code = phoneCountryCode || COUNTRY_CODE.RWANDA;

            try {
              return isValidPhoneNumberForCountry(
                value,
                code as CountryCode,
                countryCode
              );
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("phone validation error:", e);
              return false;
            }
          }
        ),
      password: yup
        .string()
        .min(
          8,
          t(
            "password-invalid-error-message",
            "Password must be at least 8 characters"
          )
        )
        .required(t("password-required-error-message", "Password is required"))
    })
  });
};

export const getRegistrationCompanySchema = (
  t: (key: string, value: string, opt?: { [key: string]: string }) => string,
  allowedBusinessTypes: readonly string[] = getRegistrationBusinessTypeValues(
    []
  ),
  organizationNameRequiredError = t(
    "company-name-required-error-message",
    "Company name is required"
  )
) => {
  const allowedValues = [...allowedBusinessTypes];

  return yup.object({
    company: yup.object({
      name: yup.string().required(organizationNameRequiredError),
      businessType: yup
        .string()
        .required(
          t("business-type-required-error-message", "Business type is required")
        )
        .oneOf(
          [...allowedValues],
          t(
            "business-type-invalid-error-message",
            "Select a supported business type"
          )
        ),
      currencyCode: getCompanyCurrencySchema(t),
      registrationNumber: yup
        .string()
        .required(
          t(
            "registration-number-required-error-message",
            "Registration number is required"
          )
        )
        .matches(
          /^[A-Za-z0-9]+$/,
          t(
            "registration-number-alphanumeric-error-message",
            "Use letters and numbers only (no spaces or symbols)"
          )
        ),
      description: yup.string(),
      vatPercentage: yup
        .number()
        .required(
          t(
            "vat-percentage-required-error-message",
            "VAT percentage is required"
          )
        ),
      vatCalculationMode: yup
        .string()
        .required(
          t(
            "vat-calculation-mode-required-error-message",
            "VAT calculation mode is required"
          )
        ),
      address: getRegistrationAddressSchema(t)
    })
  });
};
